import type { TranslationKey } from '../../i18n/he';
import type { ArmorName, WeaponName } from '../combat';

/*
 * הגדרות מבנים.
 *
 * מבנה חדש = רשומה אחת כאן. אותו עיקרון כמו ביחידות: המנוע מפעיל לפי אינדקס
 * ואינו מכיר אף מבנה בשמו.
 *
 * **מגדל הגנה אינו מערכת חדשה** — הוא מבנה עם `weapon`. מערכת הקרב כבר יודעת
 * לרכוש מטרה, לירות ולחשב נזק דרך מטריצת `warhead × armor`, ולכן פילבוקס יהיה
 * קטלני לחי"ר וחסר תועלת מול שריון בלי שורת קוד חדשה — רק בזכות ה-warhead שנבחר לו.
 */

export type BuildingSprite =
    | 'yard'
    | 'refinery'
    | 'power'
    | 'barracks'
    | 'factory'
    | 'turret'
    | 'airfield'
    | 'aaGun';

export interface BuildingDef {
    readonly id: string;
    readonly nameKey: TranslationKey;
    /**
     * משפט אחד: מה המבנה עושה ולמה בונים אותו.
     *
     * **זה לא קישוט.** בר שיחק ולא ידע מה כל תחנה שהוא מקים עושה — סרגל שמראה
     * שם ומחיר בלבד מבקש מהשחקן לנחש. התיאור יושב בתוכן ולא ב-UI, ולכן מבנה
     * חדש **חייב** להסביר את עצמו כדי להתקמפל.
     */
    readonly descKey: TranslationKey;
    /** תפיסה באריחים. מבנה תופס שטח, לא נקודה. */
    readonly w: number;
    readonly h: number;
    readonly hp: number;
    readonly armor: ArmorName;
    readonly cost: number;
    /** חשמל שמיוצר. נכנס לתוקף ב-M5. */
    readonly powerProduced: number;
    /** חשמל שנצרך. */
    readonly powerConsumed: number;
    /** מקבל קוצרים ומזכה באשראי. */
    readonly acceptsOre: boolean;
    /** נשק, למגדלי הגנה. null = מבנה אזרחי. */
    readonly weapon: WeaponName | null;
    readonly sightRange: number;
    readonly sprite: BuildingSprite;
    /** מבנים שחייבים להיות בנויים כדי שאפשר יהיה להזמין אותו. */
    readonly requires: readonly string[];
    /**
     * האם אפשר להזמין אותו בסרגל הבנייה.
     *
     * המפקדה מגיעה עם הבסיס ואינה נבנית. בלי הדגל הזה היא הופיעה בקטלוג במחיר 0,
     * ולחיצה עליה הייתה נותנת מפקדה נוספת בחינם.
     */
    readonly buildable: boolean;
}

export const BUILDING_DEFS = [
    {
        id: 'yard',
        nameKey: 'building_yard',
        descKey: 'building_yard_desc',
        w: 3,
        h: 3,
        hp: 1000,
        armor: 'structure',
        cost: 0,
        powerProduced: 0,
        powerConsumed: 0,
        acceptsOre: false,
        weapon: null,
        sightRange: 8 * 256,
        sprite: 'yard',
        requires: [],
        // מגיעה עם הבסיס, לא נבנית.
        buildable: false,
    },
    {
        id: 'refinery',
        nameKey: 'building_refinery',
        descKey: 'building_refinery_desc',
        w: 3,
        /*
         * **3×3 ולא 3×2.** היסט של מבנה מסתובב ברבע סיבוב בצורה מדויקת אך ורק
         * כש-`w % 2 === h % 2`; פריות שונות נותנות פינה שברירית, ובמפות של
         * ארבעה שחקנים זה שובר את ההוגנות. ב-180 מעלות זה תמיד יצא שלם, ולכן
         * הבעיה לא התגלתה עד עכשיו.
         */
        h: 3,
        hp: 700,
        armor: 'structure',
        cost: 2000,
        powerProduced: 0,
        powerConsumed: 30,
        acceptsOre: true,
        weapon: null,
        sightRange: 6 * 256,
        sprite: 'refinery',
        requires: ['yard'],
        buildable: true,
    },
    {
        id: 'power',
        nameKey: 'building_power',
        descKey: 'building_power_desc',
        w: 2,
        h: 2,
        hp: 500,
        armor: 'structure',
        cost: 300,
        powerProduced: 100,
        powerConsumed: 0,
        acceptsOre: false,
        weapon: null,
        sightRange: 5 * 256,
        sprite: 'power',
        requires: ['yard'],
        buildable: true,
    },
    {
        id: 'barracks',
        nameKey: 'building_barracks',
        descKey: 'building_barracks_desc',
        w: 2,
        h: 2,
        hp: 600,
        armor: 'structure',
        cost: 500,
        powerProduced: 0,
        powerConsumed: 20,
        acceptsOre: false,
        weapon: null,
        sightRange: 5 * 256,
        sprite: 'barracks',
        requires: ['yard', 'power'],
        buildable: true,
    },
    {
        id: 'factory',
        nameKey: 'building_factory',
        descKey: 'building_factory_desc',
        w: 3,
        // ריבועי מאותה סיבה כמו מפעל העיבוד — ראו ההערה שם.
        h: 3,
        hp: 800,
        armor: 'structure',
        cost: 2000,
        powerProduced: 0,
        powerConsumed: 50,
        acceptsOre: false,
        weapon: null,
        sightRange: 5 * 256,
        sprite: 'factory',
        requires: ['yard', 'refinery'],
        buildable: true,
    },
    {
        /*
         * פילבוקס: קטלני לחי"ר, כמעט חסר תועלת מול שריון — **בלי שורת קוד**.
         * כל ההבדל הוא ה-warhead שנבחר לנשק שלו, דרך מטריצת warhead × armor.
         */
        id: 'pillbox',
        nameKey: 'building_pillbox',
        descKey: 'building_pillbox_desc',
        w: 1,
        h: 1,
        hp: 400,
        armor: 'structure',
        cost: 500,
        powerProduced: 0,
        powerConsumed: 10,
        acceptsOre: false,
        weapon: 'rifle',
        sightRange: 6 * 256,
        sprite: 'turret',
        requires: ['yard', 'barracks'],
        buildable: true,
    },
    {
        /** מגדל תותח: היפוך התמונה — הורג רכב, מבזבז פגזים על חי"ר. */
        id: 'gunTurret',
        nameKey: 'building_turret',
        descKey: 'building_turret_desc',
        w: 1,
        h: 1,
        hp: 500,
        armor: 'structure',
        cost: 900,
        powerProduced: 0,
        powerConsumed: 40,
        acceptsOre: false,
        weapon: 'tankCannon',
        sightRange: 7 * 256,
        sprite: 'turret',
        requires: ['yard', 'factory'],
        buildable: true,
    },
    /*
     * **בסוף המערך, תמיד** — האינדקס הוא המזהה שנשמר בקובצי השמירה.
     */
    {
        id: 'airfield',
        nameKey: 'building_airfield',
        descKey: 'building_airfield_desc',
        w: 3,
        h: 3,
        hp: 900,
        armor: 'structure',
        cost: 2200,
        powerProduced: 0,
        powerConsumed: 60,
        acceptsOre: false,
        weapon: null,
        sightRange: 6 * 256,
        sprite: 'airfield',
        // דורש מפעל רק"ם: הציר האווירי הוא שכבה מעל צבא קרקע, לא קיצור דרך אליו.
        requires: ['yard', 'factory'],
        buildable: true,
    },
    {
        id: 'aaGun',
        nameKey: 'building_aa_gun',
        descKey: 'building_aa_gun_desc',
        w: 1,
        h: 1,
        hp: 420,
        armor: 'structure',
        cost: 800,
        powerProduced: 0,
        powerConsumed: 30,
        acceptsOre: false,
        weapon: 'flakCannon',
        sightRange: 8 * 256,
        sprite: 'aaGun',
        /*
         * דורש **קסרקטין בלבד** — זול ומוקדם בכוונה. אם ההגנה מאוויר תגיע רק
         * אחרי מפעל רק"ם, מי שממהר למסוקים מנצח לפני שיש למי לענות לו.
         */
        requires: ['yard', 'barracks'],
        buildable: true,
    },
] as const satisfies readonly BuildingDef[];

export type BuildingName = (typeof BUILDING_DEFS)[number]['id'];

/** אינדקס בתוך BUILDING_DEFS. זה מה שנשמר במצב ונכנס ל-hash. */
export type BuildingTypeId = number;

export const BUILDING_BY_NAME: Readonly<Record<BuildingName, BuildingTypeId>> = Object.fromEntries(
    BUILDING_DEFS.map((def, index) => [def.id, index]),
) as Record<BuildingName, BuildingTypeId>;

export function buildingDef(id: BuildingTypeId): BuildingDef {
    const def = BUILDING_DEFS[id];
    if (!def) throw new Error(`מבנה לא מוכר: ${id}`);
    return def;
}
